/**
 * Phone Number Normalization Utility
 *
 * This utility is a core component of the enhanced contact matching solution.
 * It addresses the issue where contacts couldn't be matched due to different phone number formats.
 *
 * Problem Context:
 * - Phone numbers are stored in various formats: +1234567890, 1234567890, +12345678901, etc.
 * - Original contact matching would fail when formats didn't match exactly
 * - International numbers with country codes weren't being recognized
 *
 * Solution:
 * - Generates multiple normalized variants of each phone number
 * - Handles US numbers (10/11 digits) and international formats
 * - Returns array of possible formats for cross-referencing during contact lookup
 *
 * Formats Generated:
 * - Digits only (e.g., "1234567890")
 * - With country code (e.g., "11234567890")
 * - Without country code (e.g., "2345678901" from "+12345678901")
 * - Original format preservation
 *
 * @param {string} phoneNumber - The phone number to normalize
 * @returns {string[]} - Array of normalized phone number variants for matching
 */
export default function normalizePhoneNumber(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return [];
  }

  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  if (digitsOnly.length < 7) {
    // Too short to be a valid phone number
    return [digitsOnly];
  }

  const variants = [];

  // Add the original string as-is (for exact matches)
  variants.push(phoneNumber);

  // Add digits only
  variants.push(digitsOnly);

  // Handle different number lengths and country codes
  if (digitsOnly.length === 10) {
    // US number without country code: 2345678901
    variants.push(digitsOnly);
    variants.push(`1${digitsOnly}`); // Add US country code
  } else if (digitsOnly.length === 11) {
    // Could be US number with country code: 12345678901
    if (digitsOnly.startsWith('1')) {
      variants.push(digitsOnly); // Keep as-is
      variants.push(digitsOnly.substring(1)); // Remove leading 1
    } else {
      // Non-US country code
      variants.push(digitsOnly);
    }
  } else if (digitsOnly.length > 11) {
    // International number with country code
    variants.push(digitsOnly);
    // Try removing various country code lengths (1-3 digits)
    for (let i = 1; i <= 3 && i < digitsOnly.length - 7; i += 1) {
      variants.push(digitsOnly.substring(i));
    }
  } else if (digitsOnly.length < 10) {
    // Short number (extensions, etc.)
    variants.push(digitsOnly);
  }

  // Add formatted versions with + prefix
  variants.forEach((variant) => {
    if (variant !== phoneNumber && !variant.startsWith('+')) {
      variants.push(`+${variant}`);
    }
  });

  // Remove duplicates while preserving order
  return [...new Set(variants)];
}
